import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Table, Select, Button, Space, Typography, Card, Input, Tooltip, Empty, Alert, Modal, DatePicker } from "antd";
import { ReloadOutlined, FilterOutlined, HistoryOutlined, EyeOutlined, CalendarOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getCurrentRole, getSelectedCompanyId } from "../../../utils/pagePermissions";
import { getAuditLogs, AuditLogItem, AuditAction } from "../../../interface/Auditlogs";
import styles from "./Auditlogpage.module.css";

dayjs.extend(utc);

const ROLE_SS_ADMIN     = "Super Super Admin";
const ROLE_SYSTEM_ADMIN = "System Admin";
const ROLE_SUPER_ADMIN  = "Super Admin";

const { Title, Text } = Typography;
const { Option } = Select;

/* ─── Action code → label/color mapping ─────────────────── */
const ACTION_MAP: Record<AuditAction, { label: string; color: string }> = {
  [AuditAction.CREATE]:    { label: "Create",     color: "#22c55e" },
  [AuditAction.UPDATE]:    { label: "Update",      color: "#3b82f6" },
  [AuditAction.SOFT_DELETE]: { label: "Soft Delete",  color: "#f97316" },
  [AuditAction.RESTORE]:   { label: "Restored",    color: "#8b5cf6" },
  [AuditAction.HARD_DELETE]: { label: "Hard Delete",  color: "#ef4444" },
  [AuditAction.ACTIVATE]:  { label: "Activated",    color: "#10b981" },
  [AuditAction.DEACTIVATE]: { label: "Deactivated",  color: "#6b7280" },
};

const parseAction = (action: string): { label: string; color: string } => {
  const key = (action || "").toUpperCase() as AuditAction;
  if (ACTION_MAP[key]) return ACTION_MAP[key];
  return { label: action ? action.replace(/_/g, " ") : "Action", color: "#9ca3af" };
};

const ENTITY_COLORS: Record<string, string> = {
  TPA:    "#3b82f6",
  Payer:  "#ec4899",
  Network: "#f59e0b",
  Policy:  "#8b5cf6",
  Doctor:  "#06b6d4",
  Claim:   "#10b981",
};

const getEntityColor = (name: string): string => {
  if (ENTITY_COLORS[name]) return ENTITY_COLORS[name];
  const firstWord = (name || "").split(" ")[0];
  return ENTITY_COLORS[firstWord] ?? "#6b7280";
};

/* ─── Component ──────────────────────────────────────────── */
const AuditLogPage: React.FC = () => {
  const role      = getCurrentRole();
  const companyId = getSelectedCompanyId() ?? null;

  const isSuperSuperAdmin = role === ROLE_SS_ADMIN;
  const isSystemAdmin     = role === ROLE_SYSTEM_ADMIN;
  const isSuperAdmin      = role === ROLE_SUPER_ADMIN;
  const canSeeFull        = isSuperSuperAdmin || isSystemAdmin;
  const systemAdminMissingCompany = isSystemAdmin && !companyId;

  const [logs, setLogs]         = useState<AuditLogItem[]>([]);

  const knownTargetTypesRef = useRef<Set<string>>(new Set());
  const [targetTypeOptions, setTargetTypeOptions] = useState<string[]>([]);

  const actionTypes = useMemo(() => Object.values(AuditAction), []);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [page, setPage]         = useState(1);
  const limit                   = 20;
  const [targetType, setTargetType] = useState<string | undefined>();
  const [actionFilter, setActionFilter] = useState<AuditAction | undefined>();
  const [actorSearch, setActorSearch] = useState<string>("");

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  /* ── View modal state ── */
  const [viewingRecord, setViewingRecord] = useState<AuditLogItem | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!canSeeFull) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { page, limit };
      if (isSystemAdmin && companyId) params.companyId = companyId;
      if (isSuperSuperAdmin && companyId) params.companyId = companyId;
      if (targetType) params.targetType = targetType;
      if (actionFilter) params.action = actionFilter;
      
      if (selectedDate) params.date = selectedDate.format("YYYY-MM-DD");

      const res = await getAuditLogs(params);
      const items = res.data?.items ?? [];
      setLogs(items);
      setTotal(res.data?.total ?? 0);

      let changed = false;
      items.forEach((l: AuditLogItem) => {
        if (l.targetType && !knownTargetTypesRef.current.has(l.targetType)) {
          knownTargetTypesRef.current.add(l.targetType);
          changed = true;
        }
      });
      if (changed) {
        setTargetTypeOptions([...knownTargetTypesRef.current].sort());
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [canSeeFull, isSystemAdmin, isSuperSuperAdmin, companyId, page, limit, targetType, actionFilter, selectedDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = useMemo(() => {
    if (!actorSearch) return logs;
    return logs.filter((l) => l.actorName.toLowerCase().includes(actorSearch.toLowerCase()));
  }, [logs, actorSearch]);

  const hasActiveClientFilters = !!actorSearch;

  /* ── Columns ── */
  const columns: ColumnsType<AuditLogItem> = [
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (v: string) => (
        <div className={styles.timeCell}>
          <span className={styles.timeDate}>{dayjs(v).format("DD MMM YYYY")}</span>
          <span className={styles.timeClock}>{dayjs(v).format("HH:mm")}</span>
        </div>
      ),
    },
    {
      title: "Account",
      dataIndex: "actorName",
      key: "actorName",
      width: 180,
      render: (name: string, row: AuditLogItem) => (
        <Space size={7}>
          <div className={styles.actorAvatar}>
            {name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <div className={styles.actorName}>{name}</div>
            <div className={styles.actorRole}>{row.actorRole}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      width: 130,
      render: (action: string) => {
        const { label, color } = parseAction(action);
        return (
          <span className={styles.actionBadge} style={{ background: color }}>
            {label}
          </span>
        );
      },
    },
    {
      title: "Rule Details",
      dataIndex: "entityName",
      key: "entityName",
      width: 180,
      render: (name: string) => {
        const entityColor = getEntityColor(name);
        return (
          <span
            className={styles.entityBadge}
            style={{
              color: entityColor,
              background: `${entityColor}18`,
              border: `1px solid ${entityColor}33`,
            }}
          >
            {name}
          </span>
        );
      },
    },
    {
      title: "Relation Name",
      dataIndex: "targetLabel",
      key: "targetLabel",
      width: 160,
      render: (label: string) => <span className={styles.targetLabel}>"{label}"</span>,
    },
    {
      title: "View",
      key: "view",
      width: 70,
      align: "center",
      render: (_: any, row: AuditLogItem) => (
        <Tooltip title="View details">
          <Button
            data-testid={`AuditLogPage-btn-view-${row.id}`}
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setViewingRecord(row)}
          />
        </Tooltip>
      ),
    },
  ];

  /* ── Guards ── */
  if (isSuperAdmin) {
    return (
      <div className={styles.guardCenter} data-testid="AuditLogPage-access-denied">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">Access denied — Super Admin cannot view the audit log.</Text>}
        />
      </div>
    );
  }
  if (systemAdminMissingCompany) {
    return (
      <div className={styles.guardPadded} data-testid="AuditLogPage-no-company">
        <Alert
          type="warning"
          showIcon
          message="No company selected"
          description="Please select a company first to view its audit logs."
        />
      </div>
    );
  }

  const hasChanges = viewingRecord?.changedFields && Object.keys(viewingRecord.changedFields).length > 0;

  return (
    <div className={styles.page} data-testid="AuditLogPage-page">

      {/* Header */}
      <div className={styles.headerRow} data-testid="AuditLogPage-header">
        <Space size={12} align="center">
          <div className={styles.headerIconWrap}>
            <HistoryOutlined className={styles.headerIcon} />
          </div>
          <div>
            <Title level={4} className={styles.headerTitle} data-testid="AuditLogPage-title">Audit Log</Title>
            <Text className={styles.headerSubtitle} data-testid="AuditLogPage-subtitle">
              {(total ?? 0).toLocaleString()} activity records
            </Text>
          </div>
        </Space>
        <Button
          data-testid="AuditLogPage-btn-refresh"
          icon={<ReloadOutlined />}
          onClick={fetchLogs}
          loading={loading}
          className={styles.refreshButton}
        >
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className={styles.filtersCard} styles={{ body: { padding: 0 } }} data-testid="AuditLogPage-filters-card">
        <div className={styles.filtersCardBody}>
          <Space wrap size={10} align="center">
            <FilterOutlined className={styles.filterIcon} />

            <Tooltip title="Shows events recorded on this calendar day">
              <div className={styles.dateFilterWrap}>
                <CalendarOutlined className={styles.dateFilterIcon} />
                <DatePicker
                  data-testid="AuditLogPage-datepicker"
                  className={styles.dateFilterInput}
                  value={selectedDate}
                  onChange={(v) => { setSelectedDate(v); setPage(1); }}
                  allowClear
                  format="DD MMM YYYY"
                  disabledDate={(d) => d.isAfter(dayjs().endOf("day"))}
                />
              </div>
            </Tooltip>

            <Tooltip title="Matches the exact rule/target type stored on the record">
              <Select
                data-testid="AuditLogPage-select-target-type"
                allowClear
                showSearch
                placeholder="Rule Details"
                className={styles.entityTypeSelect}
                popupMatchSelectWidth={false}
                value={targetType}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(v) => { setTargetType(v); setPage(1); }}
              >
                {targetTypeOptions.map((t) => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </Select>
            </Tooltip>

            <Select
              data-testid="AuditLogPage-select-action-filter"
              allowClear
              placeholder="Action"
              className={styles.entityTypeSelect}
              popupMatchSelectWidth={false}
              value={actionFilter}
              onChange={(v) => { setActionFilter(v); setPage(1); }}
            >
              {actionTypes.map((a) => (
                <Option key={a} value={a}>{parseAction(a).label}</Option>
              ))}
            </Select>

            <Input
              data-testid="AuditLogPage-input-actor-search"
              placeholder="Search by Account…"
              allowClear
              className={styles.actorSearchInput}
              value={actorSearch}
              onChange={(e) => setActorSearch(e.target.value)}
            />
            <Button
              data-testid="AuditLogPage-btn-clear-filters"
              type="text"
              danger
              onClick={() => {
                setTargetType(undefined);
                setActionFilter(undefined);
                setActorSearch("");
                setSelectedDate(null);
                setPage(1);
              }}
            >
              Clear
            </Button>
          </Space>
          {hasActiveClientFilters && (
            <Text type="secondary" className={styles.clientFilterHint} data-testid="AuditLogPage-client-filter-hint">
              Showing {filtered.length} of {logs.length} loaded records after the Account name search
            </Text>
          )}
        </div>
      </Card>

      {error && <Alert data-testid="AuditLogPage-error-alert" type="error" showIcon message={error} className={styles.errorAlert} />}

      {/* Table */}
      <Card className={styles.tableCard} styles={{ body: { padding: 0 } }} data-testid="AuditLogPage-table-card">
        <Table<AuditLogItem>
          data-testid="AuditLogPage-table"
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
            showTotal: (t, [s, e]) => `${s}–${e} of ${t} records`,
            className: styles.tablePagination,
          }}
          rowClassName={(_, i) => (i % 2 === 0 ? "" : styles.auditRowAlt)}
          className={styles.table}
        />
      </Card>

      {/* View Details Modal */}
      <Modal
        data-testid="AuditLogPage-view-modal"
        open={!!viewingRecord}
        onCancel={() => setViewingRecord(null)}
        footer={
          <Button data-testid="AuditLogPage-modal-btn-close" onClick={() => setViewingRecord(null)} className={styles.modalCloseBtn}>
            Close
          </Button>
        }
        width={620}
        closable={false}
        title={null}
      >
        {viewingRecord && (
          <>
            {/* Header */}
            <div className={styles.modalHeader} data-testid="AuditLogPage-modal-header">
              <Space size={10} align="center">
                <div className={styles.modalIconWrap}>
                  <EyeOutlined />
                </div>
                <span className={styles.modalTitleText} data-testid="AuditLogPage-modal-entity-name">{viewingRecord.entityName}</span>
              </Space>
              
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Space size={8} align="center">
                  <div className={styles.actorAvatar}>
                    {viewingRecord.actorName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className={styles.modalActorName} data-testid="AuditLogPage-modal-actor-name">{viewingRecord.actorName}</div>
                    <div className={styles.modalActorTime} data-testid="AuditLogPage-modal-actor-time">
                      {dayjs(viewingRecord.createdAt).format("DD/MM/YY · HH:mm")}
                    </div>
                  </div>
                </Space>
                <button 
                  onClick={() => setViewingRecord(null)}
                  style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#9ca3af", padding: "0 4px" }}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className={styles.modalBody} data-testid="AuditLogPage-modal-body">
              {hasChanges ? (
                <>
                  <div className={styles.modalSectionLabel}>CHANGES</div>
                  {Object.entries(viewingRecord.changedFields!).map(([field, val]: [string, any]) => (
                    <div key={field} className={styles.modalChangeBlock} data-testid={`AuditLogPage-modal-change-${field}`}>
                      <div className={styles.modalChangeFieldName}>{field}</div>
                      <div className={styles.modalChangeValues}>
                        <div className={styles.modalChangeCol}>
                          <div className={styles.modalChangeLabel}>Previous</div>
                          <div className={styles.modalChangeOldBox}>{String(val.old)}</div>
                        </div>
                        <div className={styles.modalChangeCol}>
                          <div className={styles.modalChangeLabel}>Current</div>
                          <div className={styles.modalChangeNewBox}>{String(val.new)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className={styles.modalSectionLabel}>RECORD</div>

                  <div className={styles.modalFieldRow} data-testid="AuditLogPage-modal-row-name">
                    <span className={styles.modalFieldName}>Name</span>
                    <span className={styles.modalFieldValue}>{viewingRecord.targetLabel}</span>
                  </div>

                  <div className={styles.modalFieldRow} data-testid="AuditLogPage-modal-row-type">
                    <span className={styles.modalFieldName}>Type</span>
                    <span className={styles.modalFieldValue}>{viewingRecord.entityName}</span>
                  </div>

                  <div className={styles.modalFieldBlock} data-testid="AuditLogPage-modal-row-details">
                    <span className={styles.modalFieldName}>Details</span>
                    <div className={styles.modalFieldValueBox}>{viewingRecord.eventDescription}</div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogPage;